import MuiTypography from '@mui/material/Typography';
import { forwardRef } from 'react';
import type { AriaAttributes, ElementType, MouseEventHandler, ReactNode } from 'react';
import { toVendor } from '../styles';
import type { ColorToken, Styleable } from '../styles';

/**
 * Text at a step of the type scale.
 *
 * `level`, not `variant`, so that the word `variant` means one thing across this
 * library (see `inputs/Button`). `tone` takes a palette role, and the incumbent's
 * deprecated `textSecondary` spelling is not in the union — `text.secondary` is
 * the one way to say it.
 */

export type TextLevel =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  | 'subtitle1' | 'subtitle2'
  | 'body1' | 'body2'
  | 'caption' | 'overline' | 'button';

/** ARIA attributes come from React's own definitions: they are kebab-case by spec. */
export interface TextProps extends Styleable, AriaAttributes {
  level?: TextLevel;
  tone?: ColorToken;
  as?: ElementType;
  gutterBottom?: boolean;
  paragraph?: boolean;
  noWrap?: boolean;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  id?: string;
  title?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  role?: string;
  children?: ReactNode;
}

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { level, tone, as, styles, vendorStyles, ...rest },
  ref,
) {
  return (
    <MuiTypography
      ref={ref}
      {...rest}
      {...(as ? { component: as } : {})}
      variant={level}
      color={tone}
      sx={toVendor(styles, vendorStyles)}
    />
  );
});
